import React, {useContext, useState, useEffect, useMemo} from 'react';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import {AppContext} from '../../contexts/AppContext';
import {translate} from '../../helpers/base';
import Discount from './Discount';

const PriceRange = ({onChange}) => {
    const {
        selectedFilters,
        setSelectedFilters,
        filteredResultsWithoutPriceFilter
    } = useContext(AppContext);

    const [selectedMinPrice, setSelectedMinPrice] = useState(0);
    const [selectedMaxPrice, setSelectedMaxPrice] = useState(0);

    // Derive max price from results (rounded up to next 10)
    const maxPrice = useMemo(() => {
        if (!filteredResultsWithoutPriceFilter?.length) return 0;
        const max = filteredResultsWithoutPriceFilter.reduce(
            (m, item) => Math.max(m, item.price || 0),
            0
        );
        const step = 10;
        return Math.ceil(max / step) * step;
    }, [filteredResultsWithoutPriceFilter]);

    // Keep local state in sync with selectedFilters.price + enforce maxPrice constraints
    useEffect(() => {
        if (!maxPrice) return;

        const priceStr = selectedFilters.price;

        // No price filter -> default to full range
        if (!priceStr) {
            setSelectedMinPrice(0);
            setSelectedMaxPrice(maxPrice);
            return;
        }

        let [filterMinPrice, filterMaxPrice] = priceStr.split('-').map(Number);

        // Base local state on current filter
        setSelectedMinPrice(filterMinPrice);
        setSelectedMaxPrice(filterMaxPrice);

        // Adjust / reset if the selected range is no longer valid vs new maxPrice
        if (filterMinPrice > maxPrice && filterMaxPrice < maxPrice) {
            // min > maxPrice, max still inside -> clamp min to 0, keep max
            setSelectedMinPrice(0);
            setSelectedFilters(prev => ({...prev, price: `0-${filterMaxPrice}`}));
        } else if (filterMinPrice > maxPrice && filterMaxPrice > maxPrice) {
            // both outside -> reset filter & range
            setSelectedMinPrice(0);
            setSelectedMaxPrice(maxPrice);
            setSelectedFilters(prev => ({...prev, price: undefined}));
        } else if (filterMaxPrice > maxPrice) {
            // max outside -> clamp, clear filter and show new max
            setSelectedMaxPrice(maxPrice);
            setSelectedFilters(prev => ({...prev, price: undefined}));
        }
    }, [maxPrice, selectedFilters.price, setSelectedFilters]);

    const updatePrices = (min, max) => {
        setSelectedMinPrice(min);
        setSelectedMaxPrice(max);
    };

    const handleSliderChange = ([min, max]) => {
        updatePrices(min, max);
    };

    const handleDragEnd = () => {
        onChange(selectedMinPrice, selectedMaxPrice);
    };

    const handleMinInputChange = (e) => {
        const value = Number(e.target.value) || 0;
        updatePrices(value, selectedMaxPrice);
    };

    const handleMaxInputChange = (e) => {
        const value = Number(e.target.value) || 0;
        updatePrices(selectedMinPrice, value);
    };

    const handleMinInputBlur = () => {
        const rounded = Math.round(selectedMinPrice / 10) * 10;
        const minPrice = Math.min(Math.max(0, rounded), selectedMaxPrice);
        updatePrices(minPrice, selectedMaxPrice);
        onChange(minPrice, selectedMaxPrice);
    };

    const handleMaxInputBlur = () => {
        const rounded = Math.round(selectedMaxPrice / 10) * 10;
        const newMax = Math.max(
            Math.min(maxPrice || 0, rounded),
            selectedMinPrice
        );
        updatePrices(selectedMinPrice, newMax);
        onChange(selectedMinPrice, newMax);
    };

    return (
        <>
            <RangeSlider
                value={[selectedMinPrice, selectedMaxPrice]}
                onInput={handleSliderChange}
                onThumbDragEnd={handleDragEnd}
                min={0}
                max={maxPrice}
                step={10}
            />
            <div className="form-group-price">
                <div className="form-group">
                    <label>{translate('accommodation-overview.filter.priceRange.min')}</label>
                    <input
                        type="text"
                        className="range-slider-min"
                        value={selectedMinPrice}
                        onChange={handleMinInputChange}
                        onBlur={handleMinInputBlur}
                    />
                </div>
                <span></span>
                <div className="form-group">
                    <label>{translate('accommodation-overview.filter.priceRange.max')}</label>
                    <input
                        type="text"
                        className="range-slider-max"
                        value={selectedMaxPrice}
                        onChange={handleMaxInputChange}
                        onBlur={handleMaxInputBlur}
                    />
                </div>
            </div>
            <div className="form-group-discount">
                <Discount />
            </div>
        </>
    );
};

export default PriceRange;
